import { FC, useMemo, useCallback } from "react";
import { find, map } from "lodash-es";

import { copy } from "shared/assets";
import { AllBands, Band, Company } from "shared/assets/icons";
import {
  ActionDropdown,
  IActionDropdownOptions,
  ACTION_DROPDOWN_TYPE,
} from "shared/components";
import {
  ActiveLabel,
  Label,
  LabelProps,
} from "Bands/containers/BandToggle/components";
import { BAND_PATHS } from "Bands/router";
import {
  useBandContext,
  useCalendarContext,
  useCompanyContext,
} from "Bands/contexts";
import { Spacer } from "shared/layout";
import { FlexContainer, Icon } from "shared/styles";
import { colors } from "shared/styles/constants";
import { ITheme } from "shared/types";
import { buildUrl } from "shared/utils";
import { IBand } from "Bands/types";

const bandToggleCopy = copy.bandToggle;

interface BandToggleProps {
  bandId: string | null;
  companyId: string | null;
}

export const BandToggle: FC<BandToggleProps> = ({ bandId, companyId }) => {
  const { openCreateBandModal, myBandUsers } = useBandContext();
  const { userCompanies } = useCompanyContext();

  const bands = useMemo(() => {
    if (myBandUsers) {
      return myBandUsers.map(({ band }) => band as unknown as IBand);
    }
    return [];
  }, [myBandUsers]);

  const label = useMemo(() => {
    if (bandId) {
      const bandTitle = find(bands, (band) => bandId === band.id)?.title;

      if (bandTitle) {
        return bandTitle;
      }
    }
    if (companyId) {
      const companyName = find(
        userCompanies,
        (company) => companyId === company.id
      )?.name;
      if (companyName) {
        return companyName;
      }
    }

    return bandToggleCopy.all;
  }, [bandId, companyId, bands, userCompanies]);

  const icon = useMemo(() => {
    if (bandId) {
      return Band;
    }
    if (companyId) {
      return Company;
    }

    return AllBands;
  }, [bandId, companyId]);

  const renderOptionLabel = useCallback(
    (isActive: boolean, props: LabelProps) => {
      if (isActive) {
        return <ActiveLabel {...props} />;
      }
      return <Label {...props} />;
    },
    []
  );

  const { date, view } = useCalendarContext();
  const queryParams = useMemo(
    () => ({
      month: date.month.toString(),
      year: date.year.toString(),
      view,
    }),
    [date.month, date.year, view]
  );

  const options: IActionDropdownOptions = useMemo(() => {
    const bandItems = bands?.map(({ title, id }) => ({
      label: renderOptionLabel(bandId === id, {
        label: title,
        icon: Band,
      }),
      handleClick: () => {},
      to: buildUrl(BAND_PATHS.BAND_CALENDAR, { bandId: id }, "", queryParams),
      dataTestId: "band_toggle_band",
    }));

    const companyItems = map(userCompanies, ({ id, name }) => ({
      label: renderOptionLabel(companyId === id, {
        label: name,
        icon: Company,
      }),
      handleClick: () => {},
      to: buildUrl(BAND_PATHS.COMPANY_DASHBOARD, { companyId: id }),
      dataTestId: "band_toggle_company",
      theme: ITheme.secondary,
    }));

    const companyItemsWithLabel = !!companyItems.length
      ? ([
          {
            type: ACTION_DROPDOWN_TYPE.LABEL,
            label: bandToggleCopy.companySwitch,
            theme: ITheme.secondary,
          },
          ...companyItems,
        ] as IActionDropdownOptions)
      : [];

    return [
      {
        label: renderOptionLabel(!(bandId || companyId), {
          label: bandToggleCopy.all,
          icon: AllBands,
        }),
        handleClick: () => {},
        to: buildUrl(BAND_PATHS.CALENDAR, {}, "", queryParams),
      },
      {
        label: renderOptionLabel(false, {
          label: (
            <FlexContainer>
              <Icon as={Band} color={colors.grey5} />
              {bandToggleCopy.createBand}
            </FlexContainer>
          ),
          isAddItem: true,
        }),
        handleClick: () => openCreateBandModal(),
        dataTestId: "create_band",
      },
      ...(bandItems || []),
      ...companyItemsWithLabel,
    ];
  }, [
    bands,
    userCompanies,
    renderOptionLabel,
    bandId,
    companyId,
    queryParams,
    openCreateBandModal,
  ]);

  return (
    <Spacer size="s" justifyContent="center">
      <ActionDropdown
        hideContainer={!(bandId || companyId)}
        size="m"
        icon={icon}
        isActive={!!(bandId || companyId)}
        label={label}
        options={options}
        noWrap={true}
        dataTestId="band_toggle_actions"
      />
    </Spacer>
  );
};
